import { useState, useEffect } from "react";
import "./BlogsAndArticles.scss";
import { useNavigate, useSearchParams } from "react-router-dom";
import Popup from "./Popup";
import { useFirestore } from "../firebase-files/hooks/useFirestore";
import { useCollection } from "../firebase-files/hooks/useCollection";
import { useAuthContext } from "../firebase-files/hooks/useAuthContext";
import { useDocument } from "../firebase-files/hooks/useDocument";
import { projectFirestore, projectStorage } from "../firebase-config/config";
import firebase from "firebase/app";
import "firebase/firestore";

import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Select from "react-select";

const BlogAndArticles = () => {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('category') || searchParams.get('catogory');

  const { updateDocument, deleteDocument } = useFirestore("blogs_and_articles");
  const { documents: blogsData } = useCollection("blogs_and_articles");
  const { documents: blogCategories } =
    useCollection("m_categories", ["source", "==", "blog"]);
  const { document: appTypeDoc } = useDocument("settings", "AppType");
  const { documents: users } = useCollection("users");

  const [appType, setAppType] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [newBlog, setNewBlog] = useState({
    title: "",
    description: "",
    link: "",
    imgUrl: "",
    category: [],
    subCategory: [],
  });

  const [selectedImage, setSelectedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState(null);

  useEffect(() => {
    if (!user || !users) {
      setIsAdmin(false);
      setIsSuperAdmin(false);
      return;
    }

    const phone = user?.phoneNumber?.replace("+91", "91");
    const userInfo = users.find((u) => u.id === phone);

    if (userInfo) {
      setIsAdmin(userInfo.role?.toLowerCase() === "admin");
      setIsSuperAdmin(userInfo.role?.toLowerCase() === "superadmin");
    } else {
      setIsAdmin(false);
      setIsSuperAdmin(false);
    }
  }, [user, users]);

  useEffect(() => {
    if (appTypeDoc) {
      if (appTypeDoc.AppType) {
        setAppType(appTypeDoc.AppType);
      } else if (appTypeDoc.apptype) {
        setAppType(appTypeDoc.apptype);
      }
    }
  }, [appTypeDoc]);

  // Check if user has permission to add based on apptype and role
  const canAdd = () => {
    if (!user) return false;

    // Default to "lite" if appType is not set yet
    const currentAppType = appType || "lite";

    if (currentAppType === "pro") {
      // For pro app, both admin and superAdmin can add
      return isAdmin || isSuperAdmin;
    } else {
      // For lite app, only superAdmin can add
      return isSuperAdmin;
    }
  };

  // Check if user has permission to delete based on role (only superAdmin can delete)
  const canDelete = () => {
    if (!user) return false;
    return isSuperAdmin;
  };

  // Filter blogs based on URL category parameter if it exists
  const filteredBlogs = blogsData?.filter(blog => {
    if (!categoryParam) return true; // Show all if no category filter

    // Decode and normalize the URL parameter
    const decodedCategory = decodeURIComponent(categoryParam)
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ' ');

    const blogCategoryList = Array.isArray(blog.category)
      ? blog.category.map(c =>
        String(c).toLowerCase().trim().replace(/\s+/g, ' ')
      )
      : [String(blog.category).toLowerCase().trim().replace(/\s+/g, ' ')];

    return blogCategoryList.some(
      cat => cat === decodedCategory
    );
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewBlog((prevBlog) => ({
      ...prevBlog,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
    }
  };

  const handleClick = () => {
    document.getElementById("hiddenFileInput").click();
  };

  const getCategoryOptions = () => {
    if (!Array.isArray(blogCategories)) return [];

    return blogCategories
      .filter((cat) => {
        const status = cat?.status?.toString().trim().toLowerCase();
        const hasCategory = cat?.category?.trim();
        return status === "active" && hasCategory;
      })
      .map((cat) => ({
        value: cat.category.trim(),
        label: cat.category.trim(),
      }));
  };

  const getSubCategoryOptions = () => {
    const selectedCategories = Array.isArray(newBlog.category)
      ? newBlog.category
      : [newBlog.category];
    let subCategoryOptions = [];

    selectedCategories.forEach((categoryValue) => {
      const categoryDoc = blogCategories?.find(
        (cat) => cat.category === categoryValue
      );
      if (categoryDoc?.subCategories) {
        const subCats = categoryDoc.subCategories
          .filter((subCat) => subCat.status === "active")
          .map((subCat) => ({
            value: subCat.subCategoryName,
            label: subCat.subCategoryName,
            category: categoryValue,
          }));
        subCategoryOptions = [...subCategoryOptions, ...subCats];
      }
    });

    return subCategoryOptions;
  };

  const handleCategorySelection = (selectedOptions) => {
    const selectedCategories = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];
    setNewBlog((prev) => ({
      ...prev,
      category: selectedCategories,
      subCategory: prev.subCategory.filter((subCat) => {
        const subCatOption = getSubCategoryOptions().find(
          (opt) => opt.value === subCat
        );
        return (
          subCatOption && selectedCategories.includes(subCatOption.category)
        );
      }),
    }));
  };

  const handleSubCategorySelection = (selectedOptions) => {
    setNewBlog((prev) => ({
      ...prev,
      subCategory: selectedOptions
        ? selectedOptions.map((option) => option.value)
        : [],
    }));
  };

  const handleSubmit = async () => {
    if (!newBlog.title || !newBlog.description || !selectedImage) {
      alert("Please fill all required fields and select an image!");
      return;
    }

    setIsUploading(true);
    try {
      const newBlogRef = await projectFirestore
        .collection("blogs_and_articles")
        .add({
          title: newBlog.title,
          description: newBlog.description,
          link: newBlog.link,
          imgUrl: "",
          category: newBlog.category,
          subCategory: newBlog.subCategory,
          createdAt: firebase.firestore.FieldValue.serverTimestamp()
        });

      if (selectedImage) {
        const newBlogDocId = newBlogRef.id;
        const storageRef = projectStorage.ref(
          `blogs_and_articles/${newBlogDocId}`
        );
        const imageRef = storageRef.child(selectedImage.name);
        await imageRef.put(selectedImage);
        const imageUrl = await imageRef.getDownloadURL();

        await newBlogRef.update({
          imgUrl: imageUrl,
        });

        setNewBlog({
          title: "",
          description: "",
          link: "",
          imgUrl: "",
          category: [],
          subCategory: [],
        });
        setSelectedImage(null);
        alert("Blog and image uploaded successfully!");
      }
    } catch (error) {
      console.error("Error uploading blog and image:", error);
      alert("Error uploading blog. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (blogToDelete) {
      try {
        await deleteDocument(blogToDelete.id);
        const storageRef = projectStorage.ref(
          `blogs_and_articles/${blogToDelete.id}`
        );
        const folderContents = await storageRef.listAll();
        const deletePromises = folderContents.items.map((item) =>
          item.delete()
        );
        await Promise.all(deletePromises);
        alert("Blog deleted successfully!");
      } catch (error) {
        console.error("Error deleting blog:", error);
        alert("Error deleting blog. Please try again.");
      } finally {
        setIsDeletePopupOpen(false);
        setBlogToDelete(null);
      }
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate();
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  console.log("AppType:", appType);
  console.log("User role:", user?.role);
  console.log("Can add:", canAdd());
  console.log("Can delete:", canDelete());

  return (
    <div>
      <Popup
        showPopupFlag={isDeletePopupOpen}
        setShowPopupFlag={setIsDeletePopupOpen}
        msg="Are you sure you want to delete this Blog?"
        setPopupReturn={(confirmed) => {
          if (confirmed) {
            handleConfirmDelete();
          }
        }}
      />
      <div className="blog-and-articles-blog_sect">
        <div className="container">
          <div className="section_title">
            <h2 className="blog-and-section_heading_effect">News & Media</h2>
            <h3 className="blog-and-section_sub-heading_effect">
              Journeying Through Untold Stories and Hidden Reals
            </h3>
          </div>

          {categoryParam && (
            <div className="category-filter-info">
              Showing blogs for category: <strong>{decodeURIComponent(categoryParam)}</strong>
            </div>
          )}

          <div className="blog-article-grid">
            {user && canAdd() && (
              <div className="blog-article-card-container">
                <div className="upload-container">
                  <input
                    id="hiddenFileInput"
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleImageChange}
                  />

                  <div
                    className="blog-media-image-upload"
                    onClick={handleClick}
                  >
                    {selectedImage ? (
                      <div className="blog-media-image-preview blog-media-card-image">
                        <img
                          src={URL.createObjectURL(selectedImage)}
                          alt="Selected"
                        />
                        <span className="blog-media-image-upload-icon-overlay material-symbols-outlined">
                          perm_media
                        </span>
                      </div>
                    ) : (
                      <div className="d-flex align-items-center justify-content-center blog-media-card-image">
                        <span className="material-symbols-outlined blog-media-image-upload-icon">
                          perm_media
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="d-flex flex-column justify-content-center align-items-center">
                    <Box
                      component="form"
                      sx={{ maxWidth: "95%" }}
                      validate
                      autoComplete="on"
                    >
                      <TextField
                        id="outlined-title"
                        label="Title"
                        variant="outlined"
                        name="title"
                        size="small"
                        value={newBlog.title}
                        onChange={handleInputChange}
                        fullWidth
                        sx={{ marginBottom: 2, marginTop: 2 }}
                        required
                      />
                      <TextField
                        id="outlined-description"
                        label="Description"
                        name="description"
                        size="small"
                        value={newBlog.description}
                        onChange={handleInputChange}
                        fullWidth
                        sx={{ marginBottom: 2 }}
                        required
                      />
                      <TextField
                        id="outlined-link"
                        label="Link"
                        variant="outlined"
                        name="link"
                        size="small"
                        value={newBlog.link}
                        onChange={handleInputChange}
                        fullWidth
                      />

                      <div style={{ marginTop: '16px' }}>
                        <Select
                          isMulti
                          options={getCategoryOptions()}
                          value={getCategoryOptions().filter((option) =>
                            newBlog.category.includes(option.value)
                          )}
                          onChange={handleCategorySelection}
                          className="category-multiselect"
                          classNamePrefix="category-select"
                          placeholder="Select categories..."
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: "45px",
                              borderColor: "#ddd",
                              "&:hover": {
                                borderColor: "var(--click-color)",
                              },
                            }),
                            menu: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                            menuList: (base) => ({
                              ...base,
                              maxHeight: "300px",
                              overflow: "auto",
                            }),
                            multiValue: (base) => ({
                              ...base,
                              backgroundColor: "var(--click-color)",
                              borderRadius: "16px",
                            }),
                            multiValueLabel: (base) => ({
                              ...base,
                              color: "white",
                              padding: "2px 8px",
                            }),
                            multiValueRemove: (base) => ({
                              ...base,
                              color: "white",
                              ":hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.2)",
                                color: "white",
                              },
                            }),
                            option: (base, { isFocused, isSelected }) => ({
                              ...base,
                              backgroundColor: isSelected
                                ? "var(--click-color)"
                                : isFocused
                                  ? "rgba(var(--click-color-rgb), 0.1)"
                                  : undefined,
                            }),
                          }}
                        />
                      </div>

                      <div style={{ margin: '2px' }}>
                        <Select
                          isMulti
                          options={getSubCategoryOptions()}
                          value={getSubCategoryOptions().filter((option) =>
                            newBlog.subCategory.includes(option.value)
                          )}
                          onChange={handleSubCategorySelection}
                          className="subcategory-multiselect"
                          classNamePrefix="subcategory-select"
                          placeholder="Select subcategories..."
                          isDisabled={newBlog.category.length === 0}
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: "45px",
                              borderColor: "#ddd",
                              "&:hover": {
                                borderColor: "var(--click-color)",
                              },
                            }),
                            menu: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                            menuList: (base) => ({
                              ...base,
                              maxHeight: "300px",
                              overflow: "auto",
                            }),
                            multiValue: (base) => ({
                              ...base,
                              backgroundColor: "var(--click-color)",
                              borderRadius: "16px",
                            }),
                            multiValueLabel: (base) => ({
                              ...base,
                              color: "white",
                              padding: "2px 8px",
                            }),
                            multiValueRemove: (base) => ({
                              ...base,
                              color: "white",
                              ":hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.2)",
                                color: "white",
                              },
                            }),
                            option: (base, { isFocused, isSelected }) => ({
                              ...base,
                              backgroundColor: isSelected
                                ? "var(--click-color)"
                                : isFocused
                                  ? "rgba(var(--click-color-rgb), 0.1)"
                                  : undefined,
                            }),
                          }}
                        />
                      </div>
                    </Box>
                  </div>

                  <br />
                  <div className="d-flex align-items-center justify-content-center">
                    <button
                      onClick={handleSubmit}
                      style={{ width: "100%" }}
                      className="btn_fill"
                      disabled={isUploading}
                    >
                      {isUploading ? "Uploading..." : "Confirm"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {filteredBlogs && filteredBlogs.length > 0 ? (
              filteredBlogs.map((blog, index) => (
                <div key={index} className="blog-article-card-container">
                  <div className="blog-media-card-image">
                    <img src={blog.imgUrl} alt={`Blog ${index + 1}`} />
                  </div>
                  <div className="blog-media-card-body">
                    <h3>{blog.title}</h3>
                    <p dangerouslySetInnerHTML={{ __html: blog.description }} />
                    <div className="blog-meta-info">
                      {blog.category && (
                        <div className="blog-categories">
                          <strong>Categories: </strong>
                          {Array.isArray(blog.category)
                            ? blog.category.join(", ")
                            : blog.category}
                        </div>
                      )}

                      {blog.subCategory && (
                        <div className="blog-subcategories">
                          <strong>Subcategories: </strong>
                          {Array.isArray(blog.subCategory)
                            ? blog.subCategory.join(", ")
                            : blog.subCategory}
                        </div>
                      )}
                      {blog.createdAt && (
                        <span className="blog-date">
                          {formatDate(blog.createdAt)}
                        </span>
                      )}
                    </div>
                    <div
                      className="d-flex justify-content-between"
                      style={{ marginTop: "auto" }}
                    >
                      <div>
                        <div
                          className="pointer d-flex align-items-center"
                          onClick={() => {
                            navigate(`/blog-details/${blog.id}`);
                          }}
                        >
                          <span className="material-symbols-outlined">
                            visibility
                          </span>
                          &nbsp; Read More
                        </div>
                      </div>
                      {user && canDelete() && (
                        <div>
                          <span
                            className="material-symbols-outlined pointer"
                            onClick={() => {
                              setBlogToDelete(blog);
                              setIsDeletePopupOpen(true);
                            }}
                          >
                            delete
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-blogs-message">
                {categoryParam
                  ? `No blogs found for category: ${decodeURIComponent(categoryParam)}`
                  : "No blogs available"}
              </div>
            )}
          </div>
        </div>
      </div>
      <br />
    </div>
  );
};

export default BlogAndArticles;